<template>
  <div class="image-uploader">
    <div class="upload-container">
      <h3 v-if="title">{{ title }}</h3>
      <div class="upload-zone" @click="openFileSelector">
        <div v-if="!imageUrl" class="upload-placeholder">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <p>{{ placeholder || 'Click to upload an image' }}</p>
        </div>
        <div v-else class="image-preview">
          <img :src="imageUrl" alt="Uploaded image" />
          <button class="remove-image" @click.stop="removeImage">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
      <p v-if="error" class="error-message">{{ error }}</p>
      <input
        type="file"
        ref="fileInput"
        style="display: none"
        accept="image/jpeg, image/png, image/gif, image/webp"
        @change="handleFileUpload"
      />
      <div v-if="uploading" class="upload-progress">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: `${uploadProgress}%` }"></div>
        </div>
        <span>{{ uploadProgress }}%</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ImageUploader",
  props: {
    title: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: "Click to upload an image"
    },
    maxFileCount: {
      type: Number,
      default: 1
    },
    bucketName: {
      type: String,
      default: "breathing"
    },
    modelValue: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      imageUrl: this.modelValue,
      error: "",
      uploading: false,
      uploadProgress: 0
    };
  },
  watch: {
    modelValue(newValue) {
      this.imageUrl = newValue;
    }
  },
  methods: {
    openFileSelector() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    
    handleFileUpload(event) {
      this.error = "";
      const file = event.target.files[0];
      
      if (!file) {
        return;
      }
      
      // Validate file type
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        this.error = "Invalid file type. Please upload an image (JPEG, PNG, GIF, or WebP).";
        this.$emit("upload-error", { message: this.error });
        return;
      }
      
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        this.error = "File size exceeds 5MB. Please upload a smaller image.";
        this.$emit("upload-error", { message: this.error });
        return;
      }
      
      this.uploadToR2(file);
    },
    
    async uploadToR2(file) {
      try {
        this.uploading = true;
        this.uploadProgress = 0;
        
        // Import cloudflareR2Service at the top of the script section
        const cloudflareR2Service = (await import('@/services/cloudflareR2')).default;
        
        // Use direct R2 upload instead of the failing API endpoint
        const uploadResponse = await cloudflareR2Service.uploadWithValidation(file);
        
        // Complete the progress
        this.uploadProgress = 100;
        
        // Process the response - cloudflareR2Service returns the URL directly
        const imageUrl = uploadResponse.fileUrl || uploadResponse.url;
        
        if (!imageUrl) {
          throw new Error('Image URL not found in response');
        }
        
        this.imageUrl = imageUrl;
        this.$emit("update:modelValue", imageUrl);
        this.$emit("upload-success", imageUrl);
        
      } catch (error) {
        this.handleUploadError(error.message || 'Upload failed');
      } finally {
        this.uploading = false;
      }
    },
    
    handleUploadError(message) {
      this.uploading = false;
      this.error = message || "Upload failed";
      this.$emit("upload-error", { message: this.error });
    },
    
    removeImage(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      this.imageUrl = "";
      this.$emit("update:modelValue", "");
      this.$emit("image-removed");
      
      // Reset file input to allow uploading the same file again
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }
    }
  }
};
</script>

<style scoped>
.image-uploader {
  margin-bottom: 1.5rem;
}

.upload-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.upload-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #f8f9fa;
  text-align: center;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-zone:hover {
  border-color: #0d6efd;
  background-color: #f0f7ff;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  color: #6c757d;
}

.upload-placeholder svg {
  color: #6c757d;
}

.image-preview {
  position: relative;
  max-width: 100%;
  width: 100%;
}

.image-preview img {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 4px;
}

.remove-image {
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.remove-image:hover {
  background-color: #c82333;
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.upload-progress {
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.progress-bar {
  flex-grow: 1;
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #0d6efd;
  transition: width 0.2s ease;
}
</style> 